import React, { useEffect, useRef, useState } from 'react';

import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { timestampToFormattedDate } from 'utils/date-utils';

import { DatepickerProps, selectedDate } from '../../types/interfaces';

type CalendarOnChangeEvent = Parameters<
  NonNullable<React.ComponentProps<typeof Calendar>['onChange']>
>[0];

const Datepicker: React.FC<DatepickerProps> = ({
  onChange,
  datepickerDate,
  showTooltips = true,
  customTooltips,
  hasHorizontalScroll = false
}) => {
  const [selectedDate, setSelectedDate] = useState<{ dateFrom: Date | null; dateUntil: Date | null }>(
    datepickerDate
      ? {
          dateFrom: timestampToFormattedDate(datepickerDate.dateFrom) ?? null,
          dateUntil: timestampToFormattedDate(datepickerDate.dateUntil) ?? null
        }
      : { dateFrom: null, dateUntil: null }
  );

  const toast = useRef<Toast>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const isoFrom = selectedDate.dateFrom instanceof Date ? selectedDate.dateFrom.toISOString() : '';
    const isoUntil = selectedDate.dateUntil instanceof Date ? selectedDate.dateUntil.toISOString() : '';
    onChange?.({ dateFrom: isoFrom, dateUntil: isoUntil });
  }, [selectedDate]);

  const handleDateChange = (
    e: CalendarOnChangeEvent,
    field: 'dateFrom' | 'dateUntil'
  ) => {
    const newDate = e.value instanceof Date ? e.value : null;

    if (newDate) {
      const otherField = field === 'dateFrom' ? 'dateUntil' : 'dateFrom';
      const otherDate = selectedDate[otherField];
      if (otherDate instanceof Date) {
        if (
          (field === 'dateFrom' && newDate > otherDate) ||
          (field === 'dateUntil' && newDate < otherDate)
        ) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Внимание',
            detail:
              field === 'dateFrom'
                ? 'Дата начала не может быть позже даты окончания.'
                : 'Дата окончания не может быть раньше даты начала.',
            life: 3000
          });
          return;
        }
      }
    }

    setSelectedDate((prev) => ({ ...prev, [field]: newDate }));
  };

  return (
    <div className="flex flex-column datepicker-container">
      <Toast ref={toast} />
      <div className="flex gap-2">
        <div className="flex-column justify-content-center">
          <Calendar
            placeholder="С даты"
            value={selectedDate.dateFrom}
            onChange={(e) => handleDateChange(e, 'dateFrom')}
            showButtonBar
            tooltip={showTooltips ? (customTooltips?.from ?? 'Фильтрация по дате регистрации наряда') : undefined}
            tooltipOptions={{ position: 'top' }}
          />
        </div>

        <div className="flex-column">
          <Calendar
            placeholder="До даты"
            value={selectedDate.dateUntil}
            onChange={(e) => handleDateChange(e, 'dateUntil')}
            showButtonBar
            tooltip={showTooltips ? (customTooltips?.until ?? 'Фильтрация по дате регистрации наряда') : undefined}
            tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
          />
        </div>
      </div>
    </div>
  );
};

export default Datepicker;
